import dayjs from 'dayjs';
// eslint-disable-next-line import/named
import { object, string, array, InferType, boolean, mixed, number } from 'yup';

import { Dimensions, DspReportLimits, Intervals } from 'modules/misc/miscApiTypes';
import { PerformanceReport } from 'modules/report/reportApiTypes';

export const HOURS = {
  0: '00:00 AM',
  1: '1:00 AM',
  2: '2:00 AM',
  3: '3:00 AM',
  4: '4:00 AM',
  5: '5:00 AM',
  6: '6:00 AM',
  7: '7:00 AM',
  8: '8:00 AM',
  9: '9:00 AM',
  10: '10:00 AM',
  11: '11:00 AM',
  12: '12:00 PM',
  13: '1:00 PM',
  14: '2:00 PM',
  15: '3:00 PM',
  16: '4:00 PM',
  17: '5:00 PM',
  18: '6:00 PM',
  19: '7:00 PM',
  20: '8:00 PM',
  21: '9:00 PM',
  22: '10:00 PM',
  23: '11:00 PM',
};

const performanceReportFieldsSchema = object().shape({
  reportType: string().trim().required(),
  name: string().required('Report name is required'),
  dateRange: object().shape({
    startDate: mixed<dayjs.Dayjs>().nullable(),
    endDate: mixed<dayjs.Dayjs>().nullable(),
    period: string(),
  }),
  showUniques: boolean(),
  methodology: string().required(),
  conversionWindow: number().required(),
  conversionSource: string().optional(),
  interval: string().required(),
  dimensions: array().required(),
  dimensionFilters: array(
    object().shape({
      metric: string(),
      value: string().optional(),
      include: array(string()).nullable(),
      exclude: array(string()).nullable(),
    }),
  ),
  metrics: array().required().min(1, 'At least one metric is required').required(),
  reportFormat: string().required(),
  isScheduled: boolean(),
  emails: array().when('isScheduled', {
    is: true,
    then: (schema) =>
      schema.min(1, 'At least one email is required').required('Email list is required'),
    otherwise: (schema) => schema,
  }),
  cadenceType: string().required(),
  dailyCadence: string().required(),
  weeklyCadence: string(),
  monthlyCadence: string(),
});

/** Field rules plus the report size limits (CAIENG-549), which need the whole form. */
export const performanceReportSchema = performanceReportFieldsSchema.test(
  'report-size-limit',
  function (values) {
    const { limits, dimensions, intervals } = (this.options.context || {}) as ReportLimitContext;
    const interval = values.interval;
    const error = getReportSizeLimitError(
      limits,
      interval,
      values.dimensions,
      values.dateRange?.startDate,
      values.dateRange?.endDate,
      (dimension) => dimensions?.[dimension]?.shownName || dimension,
      intervals?.[interval] || interval,
    );

    return error ? this.createError({ path: error.field, message: error.message }) : true;
  },
);

/** Passed as react-hook-form `context` so the schema can read the BE's report limits. */
export type ReportLimitContext = {
  limits?: DspReportLimits;
  dimensions?: Dimensions;
  intervals?: Intervals;
};

/**
 * Mirrors the BE's ReportSizeLimits (CAIENG-549): a report that covers too many days for its interval and
 * dimensions runs the backend out of memory, so the BE rejects it. Checking here shows the same rule inline.
 * Returns null when the report is allowed or the DSP has no limits.
 */
export const getReportSizeLimitError = (
  limits: DspReportLimits | undefined,
  interval: string | undefined,
  selectedDimensions: string[] | undefined,
  startDate: dayjs.Dayjs | null | undefined,
  endDate: dayjs.Dayjs | null | undefined,
  shownName: (dimension: string) => string,
  intervalName: string,
): { field: 'dateRange' | 'dimensions'; message: string } | null => {
  const limit = interval ? limits?.intervals[interval] : undefined;

  if (!limits || !limit || !startDate || !endDate) return null;

  const heavy = (selectedDimensions || []).filter((d) => limits.heavyDimensions.includes(d));
  const heavyNames = heavy.map(shownName).join(', ');
  const maxDays = heavy.length ? limit.withHeavyDays : limit.lightOnlyDays;

  if (maxDays === 0) {
    return {
      field: 'dimensions',
      message: `${intervalName} reports can't include ${heavyNames}. Remove ${
        heavy.length === 1 ? 'it' : 'them'
      } or choose a Daily or Cumulative interval.`,
    };
  }

  const days = dayjs(endDate).startOf('day').diff(dayjs(startDate).startOf('day'), 'day') + 1;

  if (days <= maxDays) return null;

  return {
    field: 'dateRange',
    message: heavy.length
      ? `${intervalName} reports with ${heavyNames} can cover at most ${maxDays} days (this one covers ${days}). Shorten the date range or remove ${
          heavy.length === 1 ? 'that dimension' : 'those dimensions'
        }.`
      : `${intervalName} reports can cover at most ${maxDays} days (this one covers ${days}). Shorten the date range.`,
  };
};
export type PerformanceFormType = InferType<typeof performanceReportSchema>;

export type CreateReportProps = {
  setIsCreateFormOpen: (b: boolean) => void;
  selectedReport: PerformanceReport | null;
  refetch: () => void;
  setReportType: (t: string) => void;
  reportType: string;
};

export type CreateReportWrapperProps = {
  setIsCreateFormOpen: (b: boolean) => void;
  selectedReport: PerformanceReport | null;
  refetch: () => void;
};

export const toEnum = (arr: number[]) => Object.fromEntries(arr.map((n) => [n, n]));

export const OVERALL = 'OVERALL';
export const NAME_MAX_LENGTH = 100;
export const CUSTOM = 'CUSTOM';
export const DEFAULT_DAILY_CADENCE = '9'; // 9:00 AM
export const DEFAULT_TIME_SLICE = 'LAST_30_DAYS';

/**
 * Determines if scheduling should be disabled for custom date ranges.
 * Scheduling is only allowed when the end date is in the future.
 *
 * @param isCustomDateRange - Whether the selected date range is custom
 * @param endDate - The end date of the date range (Dayjs object or null)
 * @returns true if scheduling should be disabled, false otherwise
 */
export const isSchedulingDisabledForCustomRange = (
  isCustomDateRange: boolean,
  endDate: dayjs.Dayjs | null | undefined,
): boolean => {
  if (!isCustomDateRange) return false;

  const today = dayjs().startOf('day');
  const endDateForComparison = endDate ? dayjs(endDate).startOf('day') : null;
  const isEndDateInFuture = endDateForComparison ? endDateForComparison.isAfter(today) : false;

  return !isEndDateInFuture;
};

export enum STATUS_TYPES {
  IN_PROGRESS = 'IN_PROGRESS',
  COMPLETED = 'COMPLETED',
  EMPTY = 'EMPTY',
}
export type ReportsTableProps = {
  setIsCreateFormOpen: (b: boolean) => void;
  selectedReport: PerformanceReport | null;
  setSelectedReport: (report: PerformanceReport | null) => void;
  refetch: () => void;
  pageSize: number;
  setPageSize: (n: number) => void;
  page: number;
  setPage: (n: number) => void;
  search: string;
  setSearch: (s: string) => void;
  setSortBy: (s: string | null) => void;
  setSortDirection: (s: string | null) => void;
};

export type AllTouchPreviewTableProps = {
  page: number;
  setPage: (n: number) => void;
  pageSize: number;
  setPageSize: (n: number) => void;
};

export type CheckboxField = { onChange: (values: string[]) => void; value: string[] };

export const allTouchReportSchema = object().shape({
  reportType: string().trim().required(),
  name: string().required('Report name is required'),
  dateRange: object().shape({
    startDate: mixed<dayjs.Dayjs>().nullable(),
    endDate: mixed<dayjs.Dayjs>().nullable(),
    period: string(),
  }),
  conversionWindow: number().required(),
  conversionSource: string().optional(),
  campaign: string().required('Campaign is required'),
  eventType: string().required(),
  subcategories: array().optional(),
  dimensionFilters: array(
    object().shape({
      metric: string(),
      value: string().optional(),
      include: array(string()).nullable(),
      exclude: array(string()).nullable(),
    }),
  ),
  reportFormat: string().required(),
  isScheduled: boolean(),
  emails: array().when('isScheduled', {
    is: true,
    then: (schema) =>
      schema.min(1, 'At least one email is required').required('Email list is required'),
    otherwise: (schema) => schema,
  }),
  cadenceType: string().required(),
  dailyCadence: string().required(),
  weeklyCadence: string(),
  monthlyCadence: string(),
});
export type AllTouchReportFormType = InferType<typeof allTouchReportSchema>;
